import { createContext, useEffect, useReducer, useCallback } from 'react';
import type { ReactNode } from 'react';
import { GetAuthorization } from 'src/api/authorize';
import { type IUser, DefaultIUser } from 'src/types/responses/loginResponse';
import { SystemModules } from 'src/types/claims/userManagerModules';
import { PermissionLevel } from 'src/types/claims/profilePermission';
import { systemId } from 'src/app.config';
import setAuthorizationToken from 'src/api/setAuthorizationToken';
import { clearCookies } from 'src/utils/cookiesHelper';
import { loginRedirect } from 'src/utils/loginRedirect';

interface IPayloadState {
  isAuthenticated: boolean;
  user: IUser;
  isAccessDenied?: boolean;
}

interface State extends Omit<IPayloadState, 'user'> {
  isInitialized: boolean;
  user: IUser | null;
  isAccessDenied?: boolean;
}

export interface AuthContextValue extends State {
  platform: 'JWT';
  logout: () => void;
  hasPermission: (permission: SystemModules) => PermissionLevel;
}

interface AuthProviderProps {
  children?: ReactNode;
}

interface InitializeAction {
  type: 'INITIALIZE';
  payload: IPayloadState;
}

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  isAccessDenied: false,
  user: null,
};

const handlers: Record<
  string,
  (state: State, action: InitializeAction) => State
> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const {
      isAuthenticated,
      user,
      isAccessDenied = false,
    } = (action as InitializeAction).payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      isAccessDenied,
      user,
    };
  },
};

const reducer = (state: State, action: InitializeAction): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'JWT',
  logout: () => {},
  hasPermission: () => PermissionLevel.None,
});

const PERMISSIONS_KEY = `metbus_permissions_${systemId}`;
const PERMISSIONS_TOKEN_KEY = `permission_token_${systemId}`;

export const AuthProvider = (props: AuthProviderProps) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    const payload: IPayloadState = {
      isAuthenticated: false,
      user: DefaultIUser,
    };

    try {
      setAuthorizationToken();
      // Check Cookie
      const cookies = document.cookie.split(';').map((c) => c.trim());
      const authTokenCookie = cookies.find((row) =>
        row.startsWith('Metbus.AuthToken=')
      );
      const tokenExists = authTokenCookie && authTokenCookie.split('=')[1];

      // No cookie -> remove session storage
      if (!tokenExists) {
        sessionStorage.removeItem(PERMISSIONS_KEY);
        sessionStorage.removeItem(PERMISSIONS_TOKEN_KEY);
      }

      const cachedPermissions = sessionStorage.getItem(PERMISSIONS_KEY);

      if (tokenExists && cachedPermissions) {
        // Has permissions and valod cookie
        const parsedPermissions = JSON.parse(cachedPermissions);

        payload.user = {
          userName: parsedPermissions.username,
          name: parsedPermissions.name,
          email: parsedPermissions.email,
          rut: parsedPermissions.rut,
          profileName: parsedPermissions.profileName,
          claims: parsedPermissions.claims || {},
          rol: parsedPermissions.rol,
        } as IUser;
        payload.isAuthenticated = true;

        dispatch({ type: 'INITIALIZE', payload: payload });
      } else {
        // Get permissions from backend, this will validate the cookie and return 401 if it's invalid or expired, triggering the SSO redirection
        const authResult = await GetAuthorization();

        if (authResult?.response === 403) {
          payload.isAccessDenied = true;
          dispatch({ type: 'INITIALIZE', payload });
          return;
        }

        if (authResult?.response === 200 && authResult.user) {
          sessionStorage.setItem(
            PERMISSIONS_KEY,
            JSON.stringify({
              username: authResult.user.userName,
              name: authResult.user.name,
              email: authResult.user.email,
              rut: authResult.user.rut,
              profileName: authResult.user.profileName,
              claims: authResult.user.claims || {},
              rol: Number(authResult.user.rol) || 0,
            })
          );

          payload.user = {
            userName: authResult.user.userName,
            name: authResult.user.name,
            email: authResult.user.email,
            rut: authResult.user.rut,
            profileName: authResult.user.profileName,
            claims: authResult.user.claims || {},
            rol: Number(authResult.user.rol) || 0,
          } as IUser;

          payload.isAuthenticated = true;
          dispatch({ type: 'INITIALIZE', payload: payload });
        } else {
          clearCookies();
          sessionStorage.removeItem(PERMISSIONS_KEY);
          sessionStorage.removeItem(PERMISSIONS_TOKEN_KEY);
          loginRedirect();
          return;
        }
      }
    } catch {
      clearCookies();
      sessionStorage.removeItem(PERMISSIONS_KEY);
      sessionStorage.removeItem(PERMISSIONS_TOKEN_KEY);
      loginRedirect();
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const logout = () => {
    const cleanReturnUrl = encodeURIComponent(window.location.origin);
    clearCookies();
    sessionStorage.removeItem(PERMISSIONS_KEY);
    sessionStorage.removeItem(PERMISSIONS_TOKEN_KEY);

    //logout redirect
    const SSO_LOGOUT_URL = `${import.meta.env.VITE_API_USERS}/api/Connect/Logout`;
    window.location.replace(
      `${SSO_LOGOUT_URL}?post_logout_redirect_uri=${cleanReturnUrl}`
    );
  };

  const hasPermission = (permission: SystemModules): PermissionLevel => {
    if (!state.user || !state.user.claims || !state.user.claims[permission])
      return PermissionLevel.None;
    return state.user.claims[permission];
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
